/** v3 API helpers for the durable streaming architecture (POST /v3/messages, SSE, cancel). */
import type { paths } from '@luxuryescapes/contract-trip'
import request from 'api/requestUtils'
import config from 'constants/config'
import { getOptions, baseUri } from './utils'
import { openSseConnection } from '../utils/sseClient'
import type { CreateMessageRequest, CreateMessageResponse, TripItineraryData } from './types'
import type { AssistantSubmitCreateMessageRequest } from 'tripPlanner/assistant/api/types'
import { toTripItineraryData } from 'tripPlanner/utils/mapTripItineraryData'

type EnsureAssistantSurfaceSnapshotRefOperation =
  paths['/api/trips/v3/assistant-surface-snapshots/ensure']['post']
export type EnsureAssistantSurfaceSnapshotRefRequest =
  EnsureAssistantSurfaceSnapshotRefOperation['requestBody']['content']['application/json']
export type EnsureAssistantSurfaceSnapshotRefResponse =
  EnsureAssistantSurfaceSnapshotRefOperation['responses']['200']['content']['application/json']['result']
export type SnapshotChunkCacheMissResponse = Extract<
  EnsureAssistantSurfaceSnapshotRefOperation['responses']['409']['content']['application/json'],
  { error: 'snapshot_chunk_cache_miss' }
>
export type SnapshotIdempotencyConflictResponse = Extract<
  EnsureAssistantSurfaceSnapshotRefOperation['responses']['409']['content']['application/json'],
  { error: 'snapshot_idempotency_conflict' }
>
export type SnapshotStoreUnavailableResponse =
  EnsureAssistantSurfaceSnapshotRefOperation['responses']['503']['content']['application/json']
export type SnapshotAuthResponse =
  | EnsureAssistantSurfaceSnapshotRefOperation['responses']['401']['content']['application/json']
  | EnsureAssistantSurfaceSnapshotRefOperation['responses']['403']['content']['application/json']

export const ASSISTANT_SURFACE_SNAPSHOT_ENSURE_TIMEOUT_MS = 750

function absoluteUrl(path: string): string {
  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  return `${endpoint}${path}`
}

function apiHostOrigin(): string {
  return (config.API_HOST.startsWith('http') ? config.API_HOST : `${config.SCHEME}://${config.API_HOST}`).replace(/\/+$/, '')
}

/**
 * Resolve the backend-provided export download URL for direct browser
 * navigation, so it works in every environment.
 *
 * The endpoint returns one of two shapes:
 *  - Our own trips-service URL (path under `/api/...`). The host it reports may
 *    be an internal/non-browser-reachable one (e.g. `docker.for.mac.host.internal`
 *    locally, or an internal service host on staging/prod), so we rewrite the
 *    host to the public API host while preserving the path/query. Auth rides on
 *    the `access_token` cookie scoped to the API host.
 *  - An external, self-authenticating URL (e.g. a presigned S3 link). This must
 *    be used verbatim — rewriting its host onto the API host strips the
 *    signature's host and makes the API host reject it with a 401.
 */
export function resolveExportDownloadUrl(pathOrUrl: string): string {
  const apiOrigin = apiHostOrigin()

  // Relative path → always served by our API host.
  if (!/^https?:\/\//i.test(pathOrUrl)) {
    const url = new URL(pathOrUrl, apiOrigin)
    return `${apiOrigin}${url.pathname}${url.search}${url.hash}`
  }

  const url = new URL(pathOrUrl)
  // One of our API URLs reported on a non-public host → rewrite to the API host.
  if (url.pathname.startsWith('/api/')) {
    return `${apiOrigin}${url.pathname}${url.search}${url.hash}`
  }

  // External self-authenticating URL (e.g. presigned S3) → use as-is.
  return pathOrUrl
}

type SubmitMessageParams = CreateMessageRequest | AssistantSubmitCreateMessageRequest
type SubmitMessageResult = CreateMessageResponse

type RenameChatOperation = paths['/api/trips/v3/chats/{chatId}']['patch']
type RenameChatRequest = RenameChatOperation['requestBody']['content']['application/json']
export type RenameChatResult = RenameChatOperation['responses']['200']['content']['application/json']['result']
type CancelMessageOperation = paths['/api/trips/v3/chats/{chatId}/cancel']['post']
type CancelMessageResult = CancelMessageOperation['responses']['200']['content']['application/json']['result']
type CancelAlreadyTerminalResponse = CancelMessageOperation['responses']['409']['content']['application/json']

function isCancelAlreadyTerminalResponse(err: unknown): err is CancelAlreadyTerminalResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'cancel_already_terminal'
}

type SendMessageOperation = paths['/api/trips/v3/messages']['post']
export type ChatBusyResponse = SendMessageOperation['responses']['409']['content']['application/json']
export type StreamingUnavailableResponse = SendMessageOperation['responses']['503']['content']['application/json']
/** svc-trip answers a body over its 10MB parser limit with this shape. */
type PayloadTooLargeResponse = SendMessageOperation['responses']['413']['content']['application/json']
type CreateTripChatExportOperation = paths['/api/trips/v3/chats/{chatId}/export']['post']
type CreateTripChatExportRequest = CreateTripChatExportOperation['requestBody']['content']['application/json']
export type CreateTripChatExportResult = CreateTripChatExportOperation['responses']['200']['content']['application/json']['result']
export type TripChatExportFormat = NonNullable<CreateTripChatExportRequest['format']>

export function isChatBusyResponse(err: unknown): err is ChatBusyResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'chat_busy'
}

export function isStreamingUnavailableResponse(err: unknown): err is StreamingUnavailableResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 503 && 'error' in err && (err.error === 'streaming_unavailable' || err.error === 'live_updates_unavailable')
}

export function isPayloadTooLargeResponse(err: unknown): err is PayloadTooLargeResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 413 && 'error' in err && err.error === 'payload_too_large'
}

export function isSnapshotChunkCacheMissResponse(err: unknown): err is SnapshotChunkCacheMissResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'snapshot_chunk_cache_miss'
}

export function isSnapshotIdempotencyConflictResponse(err: unknown): err is SnapshotIdempotencyConflictResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 409 && 'error' in err && err.error === 'snapshot_idempotency_conflict'
}

export function isSnapshotStoreUnavailableResponse(err: unknown): err is SnapshotStoreUnavailableResponse {
  return err != null && typeof err === 'object' && 'status' in err && err.status === 503 && 'error' in err && err.error === 'snapshot_store_unavailable'
}

export function isSnapshotAuthResponse(err: unknown): err is SnapshotAuthResponse {
  return err != null && typeof err === 'object' && 'status' in err && 'error' in err &&
    ((err.status === 401 && err.error === 'unauthorized') || (err.status === 403 && err.error === 'Forbidden'))
}

/**
 * POST /v3/assistant-surface-snapshots/ensure — publish or reuse an assistant
 * surface snapshot and return a usable snapshotRef. Throws the parsed error
 * body on a non-2xx response; use isSnapshotChunkCacheMissResponse to narrow
 * the one recoverable 409 shape at the call site. The other typed guards
 * (auth, idempotency conflict, store unavailable) all degrade identically at
 * the call site too — they exist only to tag distinct analytics outcomes,
 * not to change recovery behaviour.
 */
export async function ensureAssistantSurfaceSnapshotRef(
  params: EnsureAssistantSurfaceSnapshotRefRequest,
  accessToken?: string,
  timeoutMs: number = ASSISTANT_SURFACE_SNAPSHOT_ENSURE_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<EnsureAssistantSurfaceSnapshotRefResponse> {
  const uri = `${baseUri}/v3/assistant-surface-snapshots/ensure`
  const response = await request
    .post<App.ApiResponse<EnsureAssistantSurfaceSnapshotRefResponse>, EnsureAssistantSurfaceSnapshotRefRequest>(
      uri,
      params,
      { ...getOptions(accessToken), timeout: timeoutMs, signal },
    )
  // requestUtils resolves undefined (rather than rejecting) when the request is
  // aborted by the caller's signal — surface that as a real AbortError instead of
  // dereferencing .result into a misleading TypeError. This endpoint's typed
  // 200-JSON contract means undefined can't arise any other way (unlike a generic
  // 204/no-content response, which would also resolve undefined). A timeout does
  // NOT hit this branch — it rejects with a TimeoutError instead (see
  // classifyEnsureFailureOutcome in useAssistantSurfaceSnapshot.ts), so it never
  // reaches this guard at all.
  if (!response) {
    throw new DOMException('The ensure request was aborted', 'AbortError')
  }
  return response.result
}

export interface SseStreamHandlers {
  onEvent: (event: string, data: string, id: string) => void
  onOpen?: () => void
  onError?: (err: unknown) => void
  onFatalError?: (err: Error) => void
}

export async function renameChat(
  params: { chatId: number } & RenameChatRequest,
  accessToken?: string,
): Promise<RenameChatResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}`
  const response = await request
    .patch<App.ApiResponse<RenameChatResult>, RenameChatRequest>(
      uri,
      { name: params.name },
      { ...getOptions(accessToken) },
    )
  return response.result
}

export async function createTripChatExport(
  params: { chatId: number } & CreateTripChatExportRequest,
  accessToken?: string,
): Promise<CreateTripChatExportResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}/export`
  const response = await request
    .post<App.ApiResponse<CreateTripChatExportResult>, CreateTripChatExportRequest>(
      uri,
      {
        fileName: params.fileName ?? null,
        format: params.format ?? 'xlsx',
      },
      { ...getOptions(accessToken) },
    )
  return {
    ...response.result,
    downloadUrl: resolveExportDownloadUrl(response.result.downloadUrl),
  }
}

/**
 * POST /v3/messages — submit a message and start a detached generation.
 * Returns 202 with assistantMessageId and streamUrl for SSE subscription.
 * Throws the parsed error body on 409 (ChatBusyResponse), 413 (PayloadTooLargeResponse)
 * or 503 (StreamingUnavailableResponse). Use isChatBusyResponse /
 * isPayloadTooLargeResponse / isStreamingUnavailableResponse to narrow at the call site.
 */
export async function submitMessage(
  params: SubmitMessageParams,
  accessToken?: string,
): Promise<SubmitMessageResult> {
  const uri = `${baseUri}/v3/messages`
  const body: SubmitMessageParams = { ...params, streamProtocol: 'attempt_v1' }
  const response = await request
    .post<App.ApiResponse<SubmitMessageResult>, SubmitMessageParams>(uri, body, { ...getOptions(accessToken) })
  return response.result
}

/**
 * GET /v3/chats/:chatId/stream — subscribe to generation events via SSE.
 * Returns a connection handle with a close() method.
 */
export function subscribeChatStream(
  chatId: number,
  cursor: string,
  accessToken: string | undefined,
  handlers: SseStreamHandlers,
  signal?: AbortSignal,
): { close: () => void } {
  const url = absoluteUrl(`${baseUri}/v3/chats/${chatId}/stream?streamProtocol=attempt_v1`)

  return openSseConnection({
    url,
    accessToken,
    cursor,
    signal,
    handlers,
  })
}

// ───────────────────────────────────────────────────────────────────────────
// Staff-only "rewind" (chat time machine)
//
// Both endpoints are part of the @luxuryescapes/contract-trip release, so their
// request/response shapes are derived from `paths`. The `itinerary` payload is the
// same `itinerary` schema returned by the v2 itinerary endpoint (see TripItineraryData).
// ───────────────────────────────────────────────────────────────────────────

type CloneChatOperation = paths['/api/trips/v3/chats/{chatId}/clone']['post']
type CloneChatRequest = CloneChatOperation['requestBody']['content']['application/json']
export type CloneChatFromMessageResult = CloneChatOperation['responses']['200']['content']['application/json']['result']
type InspectSnapshotOperation = paths['/api/trips/v3/chats/{chatId}/snapshots/{messageId}/itinerary']['get']
type InspectSnapshotResult = InspectSnapshotOperation['responses']['200']['content']['application/json']['result']

/**
 * POST /v3/chats/:chatId/clone — fork a trip as it stood at an earlier assistant turn
 * (inclusive) into a NEW chat owned by the requesting staff member. Does not mutate the
 * source. Returns the new chat + its itinerary.
 */
export async function cloneChatFromMessage(
  params: { chatId: number; toMessageId: string; region?: string },
  accessToken?: string,
): Promise<CloneChatFromMessageResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}/clone`
  const body: CloneChatRequest = {
    toMessageId: params.toMessageId,
    ...(params.region !== undefined ? { region: params.region } : {}),
  }
  const response = await request
    .post<App.ApiResponse<CloneChatFromMessageResult>, CloneChatRequest>(uri, body, { ...getOptions(accessToken) })
  return response.result
}

/**
 * GET /v3/chats/:chatId/snapshots/:messageId/itinerary — read-only "time travel". Returns
 * the itinerary as it stood at the given assistant turn without mutating anything.
 */
export async function inspectSnapshotItinerary(
  params: { chatId: number; messageId: string; region?: string },
  accessToken?: string,
): Promise<TripItineraryData> {
  const query = params.region !== undefined ? `?region=${encodeURIComponent(params.region)}` : ''
  const uri = `${baseUri}/v3/chats/${params.chatId}/snapshots/${params.messageId}/itinerary${query}`
  const response = await request
    .get<App.ApiResponse<InspectSnapshotResult>>(uri, { ...getOptions(accessToken) })
  return toTripItineraryData(response.result.itinerary)
}

/**
 * POST /v3/chats/:chatId/cancel — cancel an in-flight generation.
 * Returns 200 with CANCELLED status. Treats 409 cancel_already_terminal with
 * currentStatus=CANCELLED as success (idempotent cancel).
 */
export async function cancelMessage(
  chatId: number,
  accessToken?: string,
): Promise<CancelMessageResult> {
  const uri = `${baseUri}/v3/chats/${chatId}/cancel`
  try {
    const response = await request
      .post<App.ApiResponse<CancelMessageResult>, Record<string, never>>(uri, {}, { ...getOptions(accessToken) })
    return response.result
  } catch(err: unknown) {
    if (isCancelAlreadyTerminalResponse(err) && err.currentStatus === 'CANCELLED') {
      return { status: 'CANCELLED', messageId: '' }
    }

    throw err
  }
}
